import { getBatches } from '../requests';
import type { Batch } from '../types';

export type WaitForBatchOptions = {
  interval?: number;
  onProgress?: (batch: Batch) => void;
  timeoutMs?: number;
  signal?: AbortSignal;
};

/**
 * Polls batch status until it is no longer in progress.
 */
export const waitForBatch = async (
  batchId: string,
  { interval = 3000, onProgress, timeoutMs, signal }: WaitForBatchOptions = {},
): Promise<Batch> => {
  if (!batchId) throw new Error('Batch id is required');

  const start = Date.now();

  const poll = async (): Promise<Batch> => {
    if (signal?.aborted) {
      throw new Error('Operation aborted');
    }
    if (timeoutMs && Date.now() - start > timeoutMs) {
      throw new Error('Batch wait timeout exceeded');
    }

    const batch = await getBatches({ pathParams: { userBatchId: batchId } });
    onProgress?.(batch);

    const status = batch?.status || '';
    if (status === 'waiting' || status === 'processing') {
      await new Promise<void>(resolve => {
        setTimeout(resolve, interval);
      });
      return poll();
    }

    return batch; // finished, finished_with_errors, or error
  };

  return poll();
};

export type HandleBatchTaskOptions = Omit<WaitForBatchOptions, 'signal'> & {
  signal?: AbortSignal;
};

/**
 * Awaits the initial request that returns a Batch (with id) and then waits until the batch is completed.
 * Accepts a starter promise returning { data: Batch }.
 *
 * @example
 * ```ts
 *   return useMutation({
 *     mutationFn: (data) =>
 *       handleBatchTask(methodToCallWithReturnBatchData(data)),
 *   });
 * ```
 */
export const handleBatchTask = async (
  starter: Promise<{ data: Batch }> | (() => Promise<{ data: Batch }>),
  options?: HandleBatchTaskOptions,
): Promise<Batch> => {
  const run =
    typeof starter === 'function'
      ? (starter as () => Promise<{ data: Batch }>)
      : () => starter as Promise<{ data: Batch }>;

  const initial = await run();
  const batchId = initial?.data?.id;
  if (!batchId) {
    throw new Error('Batch id not found in initial response');
  }

  return waitForBatch(batchId, options);
};
